import React, { useEffect, useMemo } from 'react';
import { Rnd } from 'react-rnd';
import ColoredQRCode from './ColoredQRCode';
import scanToApplyIcon from '../../assets/qr/scantoapply.png';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import TextAlign from '@tiptap/extension-text-align';

// Custom FontSize extension so textStyle fontSize marks render correctly
const FontSize = TextStyle.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      fontSize: {
        default: null,
        parseHTML: el => el.style.fontSize || null,
        renderHTML: attrs => {
          if (!attrs.fontSize) return {};
          return { style: `font-size: ${attrs.fontSize}` };
        }
      }
    };
  }
});

const TiptapElement = ({ content }) => {
  const editor = useEditor({
    extensions: [
      StarterKit,
      FontSize,
      Color,
      TextAlign.configure({ types: ['heading', 'paragraph'] })
    ],
    content,
    editable: false // Preview mode only
  });

  useEffect(() => {
    if (editor && content && JSON.stringify(editor.getJSON()) !== JSON.stringify(content)) {
      editor.commands.setContent(content);
    }
  }, [content, editor]);

  if (!editor) {
    return null;
  }

  return (
    <div style={{ width: '100%', height: '100%', pointerEvents: 'none' }}>
      <EditorContent editor={editor} style={{ width: '100%', height: '100%' }} />
    </div>
  );
};

const CanvasElement = ({ element, zoom, keyword, defaultABT }) => {

  const renderContent = () => {
    if (element.type === 'text') {
      const textContainerStyleProps = element.props?.containerStyle || {};
      return (
        <div style={{ ...textContainerStyleProps, width: '100%', height: '100%', boxSizing: 'border-box' }}>
          <TiptapElement 
            content={element.content} 
            zoom={zoom}
          />
        </div>
      );
    } else if (element.type === 'qr') {
      const qrSize = Math.min(element.layout.w, element.layout.h);
      const iconSizeRatio = element.props?.iconSizeRatio || 0.25;
      const containerStyle = element.props?.containerStyle || {};
      
      const borderSize = containerStyle.border ? parseInt(containerStyle.border, 10) : 0;
      const paddingSize = containerStyle.padding ? parseInt(containerStyle.padding, 10) : 0;
      const labelHeight = element.props?.showLabel ? 32 : 0;
      const innerSize = Math.max(10, qrSize - (borderSize * 2) - (paddingSize * 2) - labelHeight);

      return (
        <div style={{ ...containerStyle, width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', boxSizing: 'border-box' }}>
          <ColoredQRCode
            value={`sms:${defaultABT}?body=${keyword}`}
            size={innerSize}
            errorLevel="H"
            color={element.props?.color || "#000000"}
            bgColor={element.props?.bgColor || "transparent"}
            finderColor={element.props?.finderColor || "#38b549"}
            icon={element.props?.showIcon === false ? undefined : scanToApplyIcon}
            iconSize={innerSize * iconSizeRatio}
          />
          {element.props?.showLabel && (
            <div style={{ marginTop: 8, fontWeight: 'bold', fontSize: 16, color: element.props?.labelColor || '#111827' }}>{element.props.labelText}</div>
          )}
        </div>
      );
    } else if (element.type === 'logo') {
      return (
        <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: element.props?.alignment || 'center' }}>
          {element.props?.url ? (
            <img
              src={element.props.url}
              alt="Logo"
              crossOrigin="anonymous"
              style={{ maxWidth: '100%', maxHeight: '100%', objectFit: element.props?.fitMode || 'contain' }}
            />
          ) : (
             <div data-export-hide="true" style={{ width: '100%', height: '100%', background: '#f0f0f0', border: '1px dashed #d9d9d9', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#888' }}>
               No logo available
             </div>
          )}
        </div>
      );
    }
  };

  return (
    <Rnd
      style={{
        zIndex: element.layout.z || 1,
        border: '2px solid transparent', // No borders since no selection
        padding: 4
      }}
      size={{ width: element.layout.w, height: element.layout.h }}
      position={{ x: element.layout.x, y: element.layout.y }}
      bounds="parent"
      scale={zoom / 100}
      disableDragging={true}
      enableResizing={false}
    >
      <div style={{ width: '100%', height: '100%', position: 'relative' }}>
        {renderContent()}
      </div>
    </Rnd>
  );
};

const CanvasStage = ({ signRef, templateSettings, zoom, keyword, defaultABT }) => {
  const { globalSettings, elements } = templateSettings;

  // Enforce letter size (8.5x11 inches @ 96dpi)
  const currentSize = { w: 816, h: 1056 };
  const margin = 96; // Approx. 1 inch
  const safeArea = {
    x: margin,
    y: margin,
    w: currentSize.w - (margin * 2),
    h: currentSize.h - (margin * 2)
  };

  const scaledElements = useMemo(() => {
    if (!elements || elements.length === 0) return [];

    let minX = Number.POSITIVE_INFINITY;
    let minY = Number.POSITIVE_INFINITY;
    let maxX = Number.NEGATIVE_INFINITY;
    let maxY = Number.NEGATIVE_INFINITY;

    elements.forEach(({ layout }) => {
      minX = Math.min(minX, layout.x);
      minY = Math.min(minY, layout.y);
      maxX = Math.max(maxX, layout.x + layout.w);
      maxY = Math.max(maxY, layout.y + layout.h);
    });

    const contentW = Math.max(1, maxX - minX);
    const contentH = Math.max(1, maxY - minY);
    const fitScale = Math.min(safeArea.w / contentW, safeArea.h / contentH);

    const offsetX = safeArea.x + ((safeArea.w - (contentW * fitScale)) / 2) - (minX * fitScale);
    const offsetY = safeArea.y + ((safeArea.h - (contentH * fitScale)) / 2) - (minY * fitScale);

    return elements.map((element) => ({
      ...element,
      layout: {
        ...element.layout,
        x: (element.layout.x * fitScale) + offsetX,
        y: (element.layout.y * fitScale) + offsetY,
        w: element.layout.w * fitScale,
        h: element.layout.h * fitScale
      }
    }));
  }, [elements, safeArea.h, safeArea.w, safeArea.x, safeArea.y]);

  return (
    <div 
      style={{
        flex: 1,
        width: '100%',
        padding: '40px 0', // padding so canvas doesn't touch edges
        background: '#e5e7eb',
        overflowY: 'auto',
        overflowX: 'auto',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <div 
        ref={signRef}
        style={{
          width: currentSize.w,
          height: currentSize.h,
          backgroundColor: globalSettings.backgroundColor,
          position: 'relative',
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
          transform: `scale(${zoom / 100})`,
          transformOrigin: 'top center',
          fontFamily: globalSettings.fontPairing?.body || 'sans-serif',
          margin: '0 auto', // Center horizontally without causing top-scroll clipping
          flexShrink: 0
        }}
        className={`sign-canvas-area template-${templateSettings.id} ${globalSettings.fontPairing?.heading?.includes('serif') ? 'font-serif' : 'font-sans'}`}
      >
        {scaledElements.map(element => (
          <CanvasElement
            key={element.id}
            element={element}
            zoom={zoom}
            keyword={keyword}
            defaultABT={defaultABT}
          />
        ))}
      </div>
    </div>
  );
};

export default CanvasStage;
